import type { BotConfig, ExchangeAccount } from "@investhinks/api-client";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Col, Collapse, Container, Row } from "reactstrap";

import { BOT_CONFIG_POPULATE, type PerformancePoint } from "../../../../api";
import useBots from "../../../../components/bots/hooks/useBots";
import ExchangeAccountCard from "../../../../components/exchangeAccounts/ExchangeAccountCard";
import useExchangeAccounts from "../../../../components/exchangeAccounts/hooks/useExchangeAccounts";
import useMarketCurrencyPairs from "../../../../components/market/hooks/useMarketCurrencyPairs";
import MarketCard from "../../../../components/market/MarketCard";
// Modals
import { useClientOnboardingModal } from "../../../../components/modals/ClientOnboardingModal";
import useNews from "../../../../components/news/hooks/useNews";
import NewsCard from "../../../../components/news/NewsCard";
import GeneralPerformanceGraphCard from "../../../../components/stats/GeneralPerformanceGraphCard";
import StateSummary from "../../../../components/stats/StateSummary";
import useHomeHistory from "../../../../hooks/useHomeHistory";
import useHomeStats from "../../../../hooks/useHomeStats";
import trustpilot from "../../../../media/misc/trustpilot.svg";
import type { RootState } from "../../../../redux/reducers/session/state";

// Local type definitions
interface CurrencyBreakdownItem {
  currency: string;
  balance: number;
}

interface ExchangeAccountsCardsProps {
  exchangeAccounts?: ExchangeAccount[];
  botsByExchangeAccountId?: Record<number | string, BotConfig>;
  isLoadingBots?: boolean;
  isLoadingExchangeAccounts?: boolean;
  isLoadingHistory?: boolean;
  historySince?: number;
}

const HOME_BOT_POPULATE = BOT_CONFIG_POPULATE.LIST.filter(field => field !== "statistics.history");

const Home: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "home" });
  const user = useSelector((state: RootState) => state.sessionState?.user);
  const { id, role } = user || {};
  const isImpersonating = useSelector((state: RootState) => !!state.sessionState?.impersonatedUserId);
  const { news, lastUpdate: lastUpdateNews, isLoading: isLoadingNews } = useNews();
  const { currencyPairs, lastUpdate: lastUpdateCurrency } = useMarketCurrencyPairs();
  const [showHiddenExchangeAccounts, setShownHiddenExhangeAccounts] = useState<boolean>(false);

  const {
    exchangeAccounts = [],
    isLoading: isLoadingAccounts,
    isRefreshing: isRefreshingAccounts,
  } = useExchangeAccounts({
    mine: true,
    depsToReset: [id],
    sort: { status: "up", "bot_configs.id": "down", "bot_configs.disabled": "down" },
    publicationState: "preview",
    cacheKey: `exchange_accounts_${id}`,
    pagination: { pageSize: 100 },
    populate: ["exchange"],
  });

  // Fetch ALL bots once instead of per-card to avoid N+1 API calls
  // Current card values must not wait for the complete sparkline history.
  const { bots = [], isLoading: isLoadingBots } = useBots({
    mine: true,
    depsToReset: [id],
    pagination: { pageSize: 100 },
    populate: HOME_BOT_POPULATE,
    cacheKey: `my_bots_summary_${id}`,
  });
  const chartBots = useMemo(() => {
    const accountIds = new Set(exchangeAccounts.filter(ea => !ea.hidden || showHiddenExchangeAccounts).map(ea => ea.id));
    return bots.filter(bot => {
      const account = bot.exchange_account;
      const accountId = typeof account === "object" && account !== null ? account.id : account;
      return accountId != null && accountIds.has(accountId);
    });
  }, [bots, exchangeAccounts, showHiddenExchangeAccounts]);
  const botHistory = useHomeHistory(id, chartBots, !isLoadingBots && !isLoadingAccounts);
  const botsWithHistory = useMemo(() => {
    const byId = new Map(botHistory.bots.map(bot => [bot.id, bot]));
    return bots.map(bot => byId.get(bot.id) ?? bot);
  }, [bots, botHistory.bots]);

  // Create a map from exchange account ID to bot for O(1) lookup
  const botsByExchangeAccountId = useMemo(() => {
    const map: Record<number | string, BotConfig> = {};
    for (const bot of botsWithHistory) {
      const ea = bot?.exchange_account;
      const eaId = typeof ea === "object" && ea !== null ? ea.id : ea;
      if (eaId && !map[eaId]) {
        map[eaId] = bot;
      }
    }
    return map;
  }, [botsWithHistory]);

  const [visibleExchangeAccounts, hiddenExchangeAccounts] = useMemo((): [ExchangeAccount[], ExchangeAccount[]] => {
    const [visible, hidden] = exchangeAccounts.reduce<[ExchangeAccount[], ExchangeAccount[]]>(
      (acc, ea) => {
        if (ea.hidden) acc[1].push(ea);
        else acc[0].push(ea);
        return acc;
      },
      [[], []]
    );
    // Sort visible accounts by bot balance (highest first)
    visible.sort((a: ExchangeAccount, b: ExchangeAccount) => {
      const balanceA = botsByExchangeAccountId[a.id]?.stats?.balance ?? 0;
      const balanceB = botsByExchangeAccountId[b.id]?.stats?.balance ?? 0;
      return balanceB - balanceA;
    });
    return [visible, hidden];
  }, [exchangeAccounts, botsByExchangeAccountId]);

  // Onboarding modal (autoOpen if not authenticated or no visible exchange accounts, and not impersonating)
  // Wait for API to load before deciding to show modal
  const hasNoBots = !isLoadingAccounts && !(role?.name === "Authenticated" && visibleExchangeAccounts.length > 0);
  const {
    Modal: OnboardingModal,
    open: openOnboardingModal,
    isOpen: isOnboardingModalOpen,
  } = useClientOnboardingModal({ autoOpen: !isImpersonating && hasNoBots });

  // Pulse the "Get Started" button when user has no bots and modal is closed
  const shouldPulseButton = hasNoBots && !isOnboardingModalOpen;
  // Once the user already has a model, the CTA becomes "start a new model"
  const hasAnyBot = !isLoadingAccounts && exchangeAccounts.length > 0;
  const ctaTitle = hasAnyBot ? t("startNewBot") : t("getStarted");

  const { summary, history: generalHistory } = useHomeStats(id);
  const { data: generalStatsResponse, isLoading: isLoadingAccumulatedProfits, isValidating: isRefreshingStats,
    error: generalStatsError, mutate: refreshGeneralStats } = summary;
  const { data: generalHistoryResponse, isLoading: isLoadingHistory, error: generalHistoryError } = generalHistory;
  const perCurrency = generalStatsResponse?.perCurrency ?? {};
  const totals = generalStatsResponse?.totals;

  // Extract accumulated profits history and build currency breakdown for tooltip
  const { accumulatedProfitsPerCurrency, currencyBreakdown } = useMemo(() => {
    const profitsPerCurrency: Record<string, PerformancePoint[]> = {};
    const breakdown: CurrencyBreakdownItem[] = [];

    for (const [currency, stats] of Object.entries(perCurrency)) {
      const history = generalHistoryResponse?.perCurrency?.[currency]?.history;
      if (history) profitsPerCurrency[currency] = history;
      if (stats.activeBalance) {
        breakdown.push({ currency, balance: stats.activeBalance });
      }
    }

    return { accumulatedProfitsPerCurrency: profitsPerCurrency, currencyBreakdown: breakdown };
  }, [perCurrency, generalHistoryResponse]);

  // Use pre-calculated totals from API (with currency conversion)
  const activeBots = {
    mainCurrency: totals?.mainCurrency,
    totalBalance: totals?.balance,
    totalDeposits: totals?.deposits,
    totalWithdrawals: totals?.withdrawals,
    count: totals?.activeBots,
    openTrades: totals?.openTrades,
    hasMultipleCurrencies: totals?.hasMultipleCurrencies,
    currencyBreakdown,
  };

  return (
    <>
      <OnboardingModal />

      <Container fluid className="dashboard-modern py-4 px-3 px-lg-4">
        {/* Main Grid Layout */}
        <div className="dashboard-grid">
          {/* Left Sidebar - Stats & Bots */}
          <div className="dashboard-sidebar">
            {/* Hero Stats Card */}
            <StateSummary
              balance={activeBots?.totalBalance}
              numBots={activeBots?.count}
              numTrades={activeBots?.openTrades}
              withdrawal={activeBots?.totalWithdrawals}
              deposit={activeBots?.totalDeposits}
              currency={activeBots?.mainCurrency}
              hasMultipleCurrencies={activeBots?.hasMultipleCurrencies}
              currencyBreakdown={activeBots?.currencyBreakdown}
              isLoading={isLoadingAccumulatedProfits}
              isRefreshing={isRefreshingStats || isRefreshingAccounts}
              hasError={!!generalStatsError && !generalStatsResponse}
              onRetry={refreshGeneralStats}
            />

            {/* Get Started CTA */}
            <div
              className={`get-started-cta ${shouldPulseButton ? "pulse-glow" : ""}`}
              onClick={() => openOnboardingModal()}
            >
              <div className="get-started-icon">
                <i className="fas fa-rocket" />
              </div>
              <div className="get-started-content">
                <div className="get-started-title">{ctaTitle}</div>
                <div className="get-started-subtitle">{t("getStartedSubtitle") || "Learn how to set up your first trading bot"}</div>
              </div>
              <div className="get-started-arrow">
                <i className="fas fa-chevron-right" />
              </div>
            </div>

            {/* Active Bots Section */}
            {visibleExchangeAccounts.length > 0 && (
              <div className="mt-3">
                <div className="section-header">
                  <h3 className="section-title">
                    <i className="fas fa-robot mr-2" style={{ opacity: 0.5 }} />
                    {t("yourBots") || "Your Bots"}
                  </h3>
                  <span className="metric-neutral">{visibleExchangeAccounts.length}</span>
                </div>
                <ExchangeAccountsCards
                  exchangeAccounts={visibleExchangeAccounts}
                  botsByExchangeAccountId={botsByExchangeAccountId}
                  isLoadingBots={isLoadingBots}
                  isLoadingExchangeAccounts={isLoadingAccounts}
                  isLoadingHistory={botHistory.isLoading}
                  historySince={botHistory.historySince}
                />
              </div>
            )}

            {/* Hidden Bots Section */}
            {hiddenExchangeAccounts.length > 0 && (
              <div className="mt-2">
                <div className="collapsible-header" onClick={() => setShownHiddenExhangeAccounts((v) => !v)}>
                  <div className="collapsible-title">
                    <i className="fas fa-eye-slash" />
                    {t("hiddenBots")} ({hiddenExchangeAccounts.length})
                  </div>
                  <i className={`fas fa-chevron-down collapsible-icon ${showHiddenExchangeAccounts ? "open" : ""}`} />
                </div>
                <Collapse isOpen={showHiddenExchangeAccounts}>
                  <div className="mt-3">
                    {showHiddenExchangeAccounts && (
                      <ExchangeAccountsCards
                        exchangeAccounts={hiddenExchangeAccounts}
                        botsByExchangeAccountId={botsByExchangeAccountId}
                        isLoadingBots={isLoadingBots}
                        isLoadingExchangeAccounts={isLoadingAccounts}
                        isLoadingHistory={botHistory.isLoading}
                        historySince={botHistory.historySince}
                      />
                    )}
                  </div>
                </Collapse>
              </div>
            )}

            {/* Empty State */}
            {visibleExchangeAccounts.length === 0 && hiddenExchangeAccounts.length === 0 && (
              <div className="card-modern">
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="fas fa-robot" />
                  </div>
                  <div className="empty-title">{t("noBots") || "No Bots Yet"}</div>
                  <div className="empty-description">
                    {t("noBotsDescription") || "Get started by adding your first trading bot"}
                  </div>
                  <button className="btn-modern btn-primary" onClick={() => openOnboardingModal()}>
                    <i className="fas fa-plus mr-2" />
                    {t("getStarted")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="dashboard-main">
            {(generalHistoryError || botHistory.error) && (
              <div className="alert alert-warning" role="alert">
                {t("historyError", "We couldn't load chart history. Current account values are shown separately.")}
                <button className="btn btn-link" onClick={() => {
                  if (generalHistoryError) void generalHistory.mutate().catch(() => undefined);
                  if (botHistory.error) void botHistory.mutate().catch(() => undefined);
                }}>{t("retry", "Retry")}</button>
              </div>
            )}
            {/* Performance Chart */}
            <GeneralPerformanceGraphCard
              data={accumulatedProfitsPerCurrency}
              mainCurrency={activeBots?.mainCurrency}
              isLoading={isLoadingAccumulatedProfits || isLoadingHistory}
            />

            {/* Widgets Row */}
            <Row>
              <Col xs={12} xl={5}>
                {/* Trustpilot Widget */}
                <a
                  href="https://es.trustpilot.com/review/investhinks.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-modern card-interactive d-block mb-4 p-3"
                  style={{ textDecoration: "none" }}
                >
                  <div className="text-center">
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "8px" }}>{t("trustpilot")}</div>
                    <img src={trustpilot} alt="Trustpilot" style={{ maxWidth: 180, width: "100%" }} />
                  </div>
                </a>

                {/* Market Widget */}
                <MarketCard currencyPairs={currencyPairs} lastUpdated={lastUpdateCurrency} />
              </Col>

              <Col xs={12} xl={7}>
                {/* News Widget */}
                <NewsCard news={news} limitToShow={5} lastUpdated={lastUpdateNews} isLoading={isLoadingNews} />
              </Col>
            </Row>
          </div>
        </div>
      </Container>
    </>
  );
};

const ExchangeAccountsCards: React.FC<ExchangeAccountsCardsProps> = ({
  exchangeAccounts = [],
  botsByExchangeAccountId = {},
  isLoadingBots,
  isLoadingExchangeAccounts,
  isLoadingHistory,
  historySince,
}) => {
  const navigate = useNavigate();

  return (
    <div className="animate-stagger-container">
      {exchangeAccounts.map((ea: ExchangeAccount, index: number) => (
        <div key={String(ea.id)} className="animate-stagger" style={{ animationDelay: `${index * 0.05}s` }}>
          <ExchangeAccountCard
            id={ea.id}
            isMine={true}
            bot={botsByExchangeAccountId[ea.id]}
            isLoadingBots={isLoadingBots}
            exchangeAccount={ea}
            isLoadingExchangeAccount={isLoadingExchangeAccounts}
            isLoadingHistory={isLoadingHistory}
            historySince={historySince}
            onClick={() => navigate(`/exchange-account/${ea.id}`)}
          />
        </div>
      ))}
    </div>
  );
};

export default Home;
